import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Select, Button, Alert, Spin, message } from 'antd';
import { shopsApi } from '../../utils/api';
import { setDeviseActive } from '../../utils/devise';
import { PAYS, DEVISES, deviseDuPays } from '../../constants/paysDevises';

// Rôles autorisés à modifier les réglages de la boutique
const ROLES_EDITION = ['owner', 'admin'];

/**
 * Réglages généraux de la boutique active : nom, pays, devise, contact et adresse.
 * La devise enregistrée est aussitôt appliquée à l'affichage des montants.
 */
const GeneralShopSettings = () => {
  const [form] = Form.useForm();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /** Charge la boutique active (identifiée par le navigateur) avec le rôle de l'utilisateur. */
  useEffect(() => {
    const loadShop = async () => {
      try {
        const activeShopId = window.localStorage.getItem('activeShopId');
        const response = await shopsApi.getAll();
        const activeShop = (response.data || []).find((item) => item.id === activeShopId);
        if (!activeShop) {
          setError('Aucune boutique active. Sélectionnez une boutique pour modifier ses réglages.');
          return;
        }
        setShop(activeShop);
        form.setFieldsValue({
          name: activeShop.name,
          country_code: activeShop.country_code,
          currency: activeShop.currency,
          phone: activeShop.phone || '',
          address: activeShop.address || '',
          website: activeShop.website || '',
        });
      } catch (loadError) {
        console.error('Erreur lors du chargement de la boutique:', loadError);
        setError('Impossible de charger les réglages de la boutique.');
      } finally {
        setLoading(false);
      }
    };
    loadShop();
  }, [form]);

  /** Propose la devise du pays choisi, modifiable ensuite. */
  const handleCountryChange = (codePays) => {
    form.setFieldsValue({ currency: deviseDuPays(codePays, form.getFieldValue('currency')) });
  };

  /** Enregistre les champs modifiables uniquement, puis synchronise la devise d'affichage. */
  const handleSave = async (values) => {
    try {
      setSaving(true);
      const payload = {
        name: values.name.trim(),
        country_code: values.country_code,
        currency: values.currency,
        phone: values.phone?.trim() || null,
        address: values.address?.trim() || null,
        website: values.website?.trim() || null,
      };
      const response = await shopsApi.update(shop.id, payload);
      setShop({ ...shop, ...response.data });
      setDeviseActive(response.data.currency);
      message.success('Réglages de la boutique enregistrés');
    } catch (saveError) {
      console.error('Erreur lors de l’enregistrement de la boutique:', saveError);
      message.error(saveError.response?.data?.error || 'Impossible d’enregistrer les réglages');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><Spin /></Card>;
  if (error) return <Card><Alert type="warning" showIcon message={error} /></Card>;

  const peutModifier = ROLES_EDITION.includes(shop.role);

  return (
    <Card title="Paramètres généraux de la boutique">
      {!peutModifier && (
        <Alert className="mb-4" type="info" showIcon message="Seuls le propriétaire et les administrateurs peuvent modifier ces réglages." />
      )}
      <Form form={form} layout="vertical" onFinish={handleSave} disabled={!peutModifier} style={{ maxWidth: 640 }}>
        <Form.Item label="Nom de la boutique" name="name" rules={[{ required: true, min: 2, message: 'Indiquez un nom d’au moins 2 caractères' }]}>
          <Input maxLength={100} />
        </Form.Item>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Form.Item label="Pays" name="country_code" rules={[{ required: true, message: 'Sélectionnez un pays' }]}>
            <Select showSearch optionFilterProp="label" onChange={handleCountryChange} options={PAYS.map((pays) => ({ value: pays.code, label: pays.label }))} />
          </Form.Item>
          <Form.Item label="Devise" name="currency" rules={[{ required: true, message: 'Sélectionnez une devise' }]}
            extra="Utilisée pour afficher tous les montants de la boutique. Les montants déjà enregistrés ne sont pas convertis.">
            <Select showSearch optionFilterProp="label" options={DEVISES.map((devise) => ({ value: devise.code, label: devise.label }))} />
          </Form.Item>
        </div>
        <Form.Item label="Téléphone" name="phone"><Input maxLength={20} /></Form.Item>
        <Form.Item label="Adresse" name="address"><Input.TextArea rows={2} maxLength={300} /></Form.Item>
        <Form.Item label="Site web" name="website"><Input placeholder="https://" /></Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>Enregistrer</Button>
      </Form>
    </Card>
  );
};

export default GeneralShopSettings;
